import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FilePlus2, FolderOpen, Clock, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function PortalDashboard() {
    const { user } = useAuth();
    const [cases, setCases] = useState([]);

    useEffect(() => {
        api.get("/case-requests/me").then((r) => setCases(r.data)).catch(() => {});
    }, []);

    const stats = {
        total: cases.length,
        open: cases.filter((c) => ["new", "in_review", "active"].includes(c.status)).length,
        resolved: cases.filter((c) => c.status === "resolved").length,
    };

    return (
        <div data-testid="portal-dashboard">
            <div className="label-tag">Welcome back</div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold tracking-tight">
                {user?.name || "Member"}.
            </h1>
            <p className="mt-3 text-white/60 max-w-xl">
                Track every case your local has opened with Bradshaw Solutions from one place.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                <Stat icon={FolderOpen} label="Total cases" value={stats.total} />
                <Stat icon={Clock} label="Open" value={stats.open} accent />
                <Stat icon={CheckCircle2} label="Resolved" value={stats.resolved} />
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
                <Link
                    to="/portal/new-request"
                    data-testid="portal-new-req-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400"
                >
                    <FilePlus2 size={16} /> Submit new request
                </Link>
                <Link
                    to="/portal/cases"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 uppercase tracking-wider text-sm hover:border-gold-500 hover:text-gold-500"
                >
                    View all cases
                </Link>
            </div>

            <div className="mt-14">
                <div className="label-tag">Latest activity</div>
                <div className="mt-4 divide-y divide-white/10 bg-ink-900 border border-white/10 rounded-2xl">
                    {cases.length === 0 ? (
                        <div className="p-10 text-center text-white/50">
                            No cases yet. Submit your first request to get started.
                        </div>
                    ) : (
                        cases.slice(0, 5).map((c) => (
                            <div key={c.id} className="p-5 flex items-center justify-between gap-4" data-testid={`portal-case-${c.id}`}>
                                <div>
                                    <div className="font-medium">{c.title}</div>
                                    <div className="text-xs text-white/50 mt-1">
                                        {new Date(c.created_at).toLocaleDateString()} · {c.service_slug}
                                    </div>
                                </div>
                                <StatusPill status={c.status} />
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <div className={`p-6 rounded-2xl border ${accent ? "border-gold-500/40 bg-gold-500/5" : "border-white/10 bg-ink-900"}`}>
            <div className="flex items-center gap-3">
                <Icon size={20} className={accent ? "text-gold-500" : "text-white/60"} />
                <div className="text-xs tracking-[0.2em] uppercase text-white/60">{label}</div>
            </div>
            <div className="mt-4 font-display text-4xl font-bold">{value}</div>
        </div>
    );
}

export function StatusPill({ status }) {
    const map = {
        new: ["border-white/20 text-white/80", "New"],
        in_review: ["border-blue-400/40 text-blue-300", "In Review"],
        active: ["border-gold-500/40 text-gold-500", "Active"],
        resolved: ["border-green-500/40 text-green-400", "Resolved"],
        closed: ["border-white/15 text-white/50", "Closed"],
    };
    const [cls, label] = map[status] || ["border-white/15 text-white/60", status];
    return (
        <span className={`text-[10px] tracking-[0.2em] uppercase font-bold border px-3 py-1 rounded-full ${cls}`}>
            {label}
        </span>
    );
}
